/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import {
  Drawer,
  Button,
  Input,
  Table,
  Form,
  message,
  Typography,
  Col,
  Row,
  Tooltip,
  Tag,
  Image,
  Divider,
  Upload,
  Spin,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useQuery, useMutation } from "@apollo/client";
import { GET_PRODUCTS, UPDATE_CATEGORY } from "./requests";
import { productTypes, categoryTypes } from "../../../../types";
import ProductList from "../../../../components/productsList";
import useUpload from "../../../../hooks/use-upload";

const { Text } = Typography;

interface EditCategoryProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  refetch: () => void;
  category: typeof categoryTypes.category | undefined;
  setCategory: (category: typeof categoryTypes.category | undefined) => void;
}

const EditCategory: React.FC<EditCategoryProps> = ({
  showModal,
  setShowModal,
  refetch,
  category,
  setCategory,
}) => {
  const [form] = Form.useForm<typeof categoryTypes.category>();
  const { upload } = useUpload();
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [searchTitle, setSearchTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageConfig, setPageConfig] = useState({ page: 1,
    pageSize: 10 });
  const [uploadLoding, setUploadLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);

  const [updateCategory] = useMutation(UPDATE_CATEGORY);

  const { data, loading: loadingProducts } = useQuery(GET_PRODUCTS, {
    variables: { params: pageConfig,
      name: searchTitle },
  });
  const products = data?.products.results || [];

  useEffect(() => {
    if (category) {
      form.setFieldsValue({
        name: category.name,
        description: category.description,
        img: category?.img,
      });
      setSelectedProducts(category?.products?.map((p) => p.id) ?? []);
    }
  }, [category, form]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      const payload = {
        variables: {
          id: category?.id,
          category: {
            ...values,
            ...(imageUrl !== null ? { img: imageUrl } : {}),
          },
          productCategories: selectedProducts,
        },
      };

      await updateCategory(payload);
      await refetch();
      message.success("Categoría actualizada exitosamente");
      onClose();
    } catch (error) {
      console.log(error);
      message.error("Error al actualizar la categoría");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async ({ fileList }) => {
    if (fileList[0]) {
      const fileName = `categories/${new Date().toISOString()}/${
        fileList[0]?.name
      }`;
      setUploadLoading(true);
      const url = await upload(fileList[0].originFileObj, fileName);

      setImageUrl(url);
    }
    setUploadLoading(false);
    message.success("Imagen actualizada.");
  };

  const onClose = () => {
    setShowModal(false);
    setSearchTitle("");
    setSelectedProducts([]);
    setCategory(undefined);
  };

  const columns = [
    {
      title: "Nombre",
      key: "name",
      render: (row: typeof productTypes.product) => {
        return <Text strong>{row.name}</Text>;
      },
    },
    {
      title: "Descripción",
      key: "description",
      render: (row: typeof productTypes.product) => {
        return <Text strong>{row.description}</Text>;
      },
    },
    {
      title: "Variantes",
      key: "variants",
      dataIndex: "variants",
      render: (variants: (typeof productTypes.product)["variants"]) => {
        const lineItems =
          variants?.map((p) => ({
            product: {
              _id: p.id,
              img: p.img,
              title: p.title,
              description: p.description,
              product: {
                name: p.product.name,
                description: p.product.description,
              },
            },
          })) ?? [];

        if (lineItems.length > 0) {
          return <ProductList lineItems={lineItems} />;
        } else {
          return <Tag color="yellow">Sin Productos</Tag>;
        }
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedProducts,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedProducts(selectedRowKeys as string[]);
    },
    preserveSelectedRowKeys: true,
  };

  return (
    <Drawer
      title="Editar Categoría"
      open={showModal}
      onClose={onClose}
      width={"60%"}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Nombre" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="description"
          label="Descripción"
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
      </Form>
      <Text>Cambiar imagen</Text>
      <Upload
        beforeUpload={() => false}
        listType="picture"
        maxCount={1}
        accept="image/*"
        onChange={(info) => {
          handleFileUpload(info);
        }}
        showUploadList={false}
      >
        <Button style={{ marginLeft: 10 }} icon={<UploadOutlined />}>
          Seleccionar Imagen
        </Button>
        {uploadLoding ? <Spin style={{ margin: 10 }} /> : null}
      </Upload>
      {imageUrl && (
        <Image style={{ marginLeft: 20 }} width={140} src={imageUrl} />
      )}
      <Divider />
      <Row>
        <Col span={18}>
          <Tooltip title="Buscar Producto por Nombre">
            <Input
              placeholder="Buscar Producto por Nombre"
              value={searchTitle}
              onChange={(e) => setSearchTitle(e.target.value)}
              allowClear
            />
          </Tooltip>
        </Col>
        <Col span={6}>
          <Text style={{ marginLeft: 10,
            fontSize: "15px" }} strong>
            Productos Seleccionados: {selectedProducts.length}
          </Text>
        </Col>
      </Row>
      <Table
        dataSource={products}
        rowKey="id"
        loading={loadingProducts}
        rowSelection={rowSelection}
        columns={columns}
        pagination={{
          current: pageConfig.page,
          pageSize: pageConfig.pageSize,
          total: data?.products?.info?.count,
          onChange: (page, pageSize) => setPageConfig({ page,
            pageSize }),
          showSizeChanger: true,
        }}
      />
      <Button
        size="large"
        loading={loading}
        type="primary"
        onClick={handleSubmit}
        style={{ marginTop: 20 }}
      >
        Actualizar Categoría
      </Button>
    </Drawer>
  );
};

export default EditCategory;
